import React, { useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';

const CARDINAL_LABELS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

const degreesToCardinal = (deg: number): string => {
  const normalized = ((deg % 360) + 360) % 360;
  const index = Math.round(normalized / 45) % 8;
  return CARDINAL_LABELS[index];
};

export const cardinalToDegrees = (dir: string): number => {
  const map: Record<string, number> = {
    N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315,
  };
  return map[dir] ?? 0;
};

const getAngleFromCenter = (cx: number, cy: number, x: number, y: number): number => {
  const rad = Math.atan2(x - cx, -(y - cy));
  return ((rad * 180) / Math.PI + 360) % 360;
};

interface WindCompassProps {
  degrees: number;
  onChange: (degrees: number) => void;
  size?: number;
}

const WindCompass: React.FC<WindCompassProps> = ({ degrees, onChange, size = 200 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const isDragging = useRef(false);

  const normalizedDeg = ((degrees % 360) + 360) % 360;
  const cardinal = degreesToCardinal(normalizedDeg);
  const center = size / 2;
  const outerRadius = size / 2 - 8;
  const innerRadius = outerRadius - 24;
  const needleLength = innerRadius - 12;
  const labelRadius = outerRadius - 12;

  const handlePointerEvent = useCallback(
    (e: React.PointerEvent<SVGSVGElement> | PointerEvent) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const angle = getAngleFromCenter(center, center, x, y);
      onChange(Math.round(angle));
    },
    [center, onChange],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      isDragging.current = true;
      (e.target as Element).setPointerCapture(e.pointerId);
      handlePointerEvent(e);
    },
    [handlePointerEvent],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!isDragging.current) return;
      handlePointerEvent(e);
    },
    [handlePointerEvent],
  );

  const onPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const needleRad = (normalizedDeg * Math.PI) / 180;
  const needleX = center + needleLength * Math.sin(needleRad);
  const needleY = center - needleLength * Math.cos(needleRad);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      <svg
        ref={svgRef}
        width={size}
        height={size}
        style={{ cursor: 'pointer', touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        {/* Outer ring */}
        <circle cx={center} cy={center} r={outerRadius} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={2} />
        {/* Inner ring */}
        <circle cx={center} cy={center} r={innerRadius} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.06)" strokeWidth={1} />

        {/* Tick marks — minor every 15°, major at cardinals */}
        {Array.from({ length: 24 }, (_, i) => {
          const angle = i * 15;
          const rad = (angle * Math.PI) / 180;
          const isCardinal = angle % 90 === 0;
          const isIntercardinal = angle % 45 === 0 && !isCardinal;
          const tickOuter = outerRadius;
          const tickInner = isCardinal ? outerRadius - 10 : isIntercardinal ? outerRadius - 7 : outerRadius - 4;

          return (
            <line
              key={angle}
              x1={center + tickOuter * Math.sin(rad)}
              y1={center - tickOuter * Math.cos(rad)}
              x2={center + tickInner * Math.sin(rad)}
              y2={center - tickInner * Math.cos(rad)}
              stroke={isCardinal ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)'}
              strokeWidth={isCardinal ? 2 : 1}
            />
          );
        })}

        {/* Cardinal / intercardinal labels */}
        {CARDINAL_LABELS.map((dir, i) => {
          const angle = i * 45;
          const rad = (angle * Math.PI) / 180;
          const isPrimary = angle % 90 === 0;
          const lx = center + labelRadius * Math.sin(rad);
          const ly = center - labelRadius * Math.cos(rad);
          const angleDiff = Math.abs(((normalizedDeg - angle + 180) % 360) - 180);
          const isNearest = angleDiff <= 22.5;

          return (
            <text
              key={dir}
              x={lx} y={ly}
              textAnchor="middle"
              dominantBaseline="central"
              fill={isNearest ? '#ff6b35' : isPrimary ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)'}
              fontSize={isPrimary ? 14 : 11}
              fontWeight={isNearest ? 700 : isPrimary ? 600 : 400}
              fontFamily="inherit"
            >
              {dir}
            </text>
          );
        })}

        {/* Center dot */}
        <circle cx={center} cy={center} r={4} fill="rgba(255,255,255,0.3)" />

        {/* Needle line */}
        <line
          x1={center} y1={center} x2={needleX} y2={needleY}
          stroke="#ff6b35"
          strokeWidth={3}
          strokeLinecap="round"
        />
        {/* Needle tip */}
        <circle cx={needleX} cy={needleY} r={6} fill="#ff6b35" />

        {/* Degree text in center */}
        <text
          x={center} y={center + 20}
          textAnchor="middle"
          fill="rgba(255,255,255,0.4)"
          fontSize={11}
          fontFamily="inherit"
        >
          {normalizedDeg}°
        </text>
      </svg>

      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
        {cardinal} — {normalizedDeg}°
      </Typography>
    </Box>
  );
};

export default WindCompass;
